/**
 * Collection Tabs Custom Element
 * Handles tab switching and collection filtering
 */

if (!customElements.get("collection-tabs")) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = [];
      this.links = [];
      this.target = null;
      this.slideshow = null;
      this.arrows = { prev: null, next: null };
    }

    connectedCallback() {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.init());
      } else {
        this.init();
      }
    }

    init() {
      this.buttons = Array.from(this.querySelectorAll("button"));
      this.links =
        this.closest(".collection-editorial-header")?.querySelectorAll(".linked-to-tab") ||
        [];
      this.target = this.dataset.target;
      this.slideshow = document.getElementById(this.target);

      if (!this.slideshow) {
        console.warn("Collection tabs: Target slideshow not found");
        return;
      }

      this.arrows.prev = document.querySelector(
        ".section-collection-tabs .slideshow-arrow--prev"
      );
      this.arrows.next = document.querySelector(
        ".section-collection-tabs .slideshow-arrow--next"
      );

      this.setupEventListeners();
      this.setupArrowListeners();
      this.initializeArrowStates();

      // Initialize Flickity
      this.initFlickity();

      // Initialize with first collection
      if (this.buttons.length > 0) {
        const firstHandle = this.buttons[0].dataset.collection;
        this.toggleCollection(firstHandle); // Use unified method

        // Wait for slideshow to initialize, then properly set up
        setTimeout(() => {
          this.selectTab(0);
        }, 200);
      }
    }

    setupEventListeners() {
      this.buttons.forEach((button, index) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          button.blur();
         
          this.selectTab(index);
        });
      });

      if (window.Shopify?.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          const index = Array.from(this.buttons).findIndex(
            (btn) =>
              btn.closest("[data-block-id]")?.dataset.blockId ===
              event.target.dataset.blockId
          );
          if (index >= 0) {
            this.selectTab(index);
          }
        });
      }
    }

    setupArrowListeners() {
      if (this.arrows.prev) {
        this.arrows.prev.addEventListener("click", () => {
          if (this.slideshow?.flickity) {
            this.slideshow.flickity.previous();
            this.updateArrowStates();
          }
        });
      }

      if (this.arrows.next) {
        this.arrows.next.addEventListener("click", () => {
          if (this.slideshow?.flickity) {
            this.slideshow.flickity.next();
            this.updateArrowStates();
          }
        });
      }
    }

    initializeArrowStates() {
      const checkFlickity = (attempts = 0) => {
        if (this.slideshow?.flickity) {
          this.setupArrowStateListeners();
        } else if (attempts < 50) {
          setTimeout(() => checkFlickity(attempts + 1), 100);
        }
      };
      checkFlickity();
    }

    setupArrowStateListeners() {
      if (!this.slideshow?.flickity) return;

      this.updateArrowStates();
      ["change", "scroll", "settle"].forEach((event) => {
        this.slideshow.flickity.on(event, () => {
          this.updateArrowStates();
          this.syncA11yState();
        });
      });
      requestAnimationFrame(() => this.syncA11yState());
    }

    updateArrowStates() {
      if (!this.slideshow?.flickity || !this.arrows.prev || !this.arrows.next)
        return;

      const flickity = this.slideshow.flickity;
      const currentIndex = flickity.selectedIndex;

      // Update arrow states
      this.arrows.prev.classList.toggle(
        "slideshow-arrow--disabled",
        currentIndex === 0
      );

      // Amended to remove complex check using currentScrollLeft and maxScrollLeft variables which have now been removed
      this.arrows.next.classList.toggle(
        "slideshow-arrow--disabled",
        flickity.selectedIndex === flickity.slides.length - 1
      );
    }

    selectTab(index) {
      if (index < 0 || index >= this.buttons.length) return;

      const button = this.buttons[index];
     
      // Removed scroll locking for better carousel touch handling

      // Update tab buttons active states and ARIA attributes
      this.buttons.forEach((btn, i) => {
        const isActive = i === index;
        btn.classList.toggle("active", isActive);
        btn.setAttribute("aria-selected", isActive);
      });

      if (this.slideshow && button.id) {
        this.slideshow.setAttribute("aria-labelledby", button.id);
      }

      if (this.links.length > 0) {
        this.links.forEach((link, i) => {
          link.classList.toggle("active", i === index);
        });
      }

      // Switch to selected collection
      button.dataset.collection &&
        this.toggleCollection(button.dataset.collection);
     
      // No scroll lock cleanup needed
    }

    toggleCollection(handle) {
      if (!this.slideshow || !handle) return;

      const currentHeight = this.slideshow.offsetHeight;
      this.slideshow.style.height = currentHeight + 'px';
      this.slideshow.style.overflow = 'hidden';

      this.slideshow.classList.add('is-switching');

      setTimeout(() => {
        if (this.slideshow.flickity) {
          this.slideshow.flickity.destroy();
          this.slideshow.flickity = null;
        }

        this.restoreProductsToTabpanels();

        const allPanels = this.slideshow.querySelectorAll(".collection-tabpanel");
        const activePanel = this.slideshow.querySelector(
          `[data-collection-panel="${handle}"]`
        );

        allPanels.forEach((panel) => {
          const isActive = panel === activePanel;
          
          if (!isActive) {
            panel.querySelectorAll(".columns").forEach((card) => {
              card.classList.remove("carousel__slide");
            });
          } else {
            activePanel.querySelectorAll(".columns").forEach((card) => {
              card.classList.add("carousel__slide");
            });
          }
        });

        allPanels.forEach((panel) => {
          const isActive = panel === activePanel;
          panel.toggleAttribute("hidden", !isActive);
          panel.classList.toggle("is-active", isActive);
        });

        requestAnimationFrame(() => {
          this.updateSlideshow();
          
          setTimeout(() => {
            if (this.slideshow) {
              this.slideshow.classList.remove('is-switching');
              
              setTimeout(() => {
                if (this.slideshow) {
                  this.slideshow.style.height = '';
                  this.slideshow.style.overflow = '';
                }
              }, 300);
            }
          }, 50);
        });
      }, 300);
    }

    restoreProductsToTabpanels() {
      const allPanels = this.slideshow.querySelectorAll(".collection-tabpanel");

      allPanels.forEach((panel) => {
        const panelHandle = panel.dataset.collectionPanel;
        const misplacedProducts = this.slideshow.querySelectorAll(
          `.columns[data-collection="${panelHandle}"]`
        );

        misplacedProducts.forEach((product) => {
          if (product.parentElement !== panel) {
            panel.appendChild(product);
          }
        });
      });
    }

    async initFlickity() {
      // Load Flickity if not already loaded
      if (!window.Flickity) {
        await this.loadFlickity();
      }

      const slides = this.slideshow.querySelectorAll(".carousel__slide");
      if (slides.length === 0) return;

      // Get column count from class name
      const columnsMatch = this.slideshow.className.match(/medium-up-(\d+)/);
      const columnCount = columnsMatch ? parseInt(columnsMatch[1]) : 4;
      const isMobile = window.innerWidth < 1020;

      this.slideshow.flickity = new Flickity(this.slideshow, {
        cellSelector: ".carousel__slide",
        cellAlign: "left", // Always left align for consistent behavior
        contain: true, // Allow partial cards to show
        pageDots: false,
        prevNextButtons: false,
        groupCells: isMobile ? 1.2 : columnCount,
        wrapAround: false,
        autoPlay: false,
        accessibility: true,
        resize: true,
        selectedAttraction: 0.025,
        friction: 0.58,
      });

      // Update progress bar on change
      this.slideshow.flickity.on("change", () => {
        this.updateProgressBar();
        this.handleLastSlidePositioning();
      });

      // Handle resize
      window.addEventListener("resize", () => {
        if (this.slideshow.flickity) this.slideshow.flickity.resize();
      });
    }

    async loadFlickity() {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://unpkg.com/flickity@2/dist/flickity.pkgd.min.js";
        script.onload = () => {
          // Load CSS if not present
          if (!document.querySelector('link[href*="flickity"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = "https://unpkg.com/flickity@2/dist/flickity.min.css";
            document.head.appendChild(link);
          }
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    updateProgressBar() {
      const progressBar = document.querySelector(".flickity-progress--bar");
      if (!progressBar || !this.slideshow?.flickity || this.slideshow.flickity.slides.length <= 1) return;

      const progress = this.slideshow.flickity.selectedIndex / (this.slideshow.flickity.slides.length - 1);
      progressBar.style.width = `${Math.max(0, Math.min(100, progress * 100))}%`;
    }

    syncA11yState() {
      window.syncFlickityCarouselA11yState?.(this.slideshow, ".carousel__slide");
    }

    handleLastSlidePositioning() {
      if (!this.slideshow?.flickity) return;
      
      const isLastSlide = this.slideshow.flickity.selectedIndex === this.slideshow.flickity.slides.length - 1;
      const isDesktop = window.innerWidth >= 768;
      
      if (isLastSlide && isDesktop) {
        // Add a small delay to ensure the slide has finished transitioning
        setTimeout(() => {
          const slider = this.slideshow.querySelector('.flickity-slider');
          if (slider) {
            // Add a class to help with positioning
            slider.classList.add('last-slide-active');
          }
        }, 100);
      } else {
        // Remove the class when not on the last slide
        const slider = this.slideshow.querySelector('.flickity-slider');
        if (slider) {
          slider.classList.remove('last-slide-active');
        }
      }
    }

    updateSlideshow() {
      // Only destroy if flickity still exists
      if (this.slideshow?.flickity) {
        this.slideshow.flickity.destroy();
        this.slideshow.flickity = null;
      }

      // Reinitialize
      setTimeout(() => {
        this.initFlickity();
        this.setupArrowStateListeners();
      }, 50);
    }
  }

  customElements.define("collection-tabs", CollectionTabs);
}


